import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { OFFER_TEMPLATES, type OfferTemplateId } from "@/types/offerTemplates";

const SELECTED_BORDER = "#e76f51";

export type OfferTemplatePickerProps = {
  value: OfferTemplateId;
  onChange: (id: OfferTemplateId) => void;
  /** Wąski panel boczny w podglądzie oferty (180px). */
  variant?: "default" | "sidebar";
};

export function OfferTemplatePicker({ value, onChange, variant = "default" }: OfferTemplatePickerProps) {
  const isSidebar = variant === "sidebar";

  return (
    <div className={cn("w-full", isSidebar ? "space-y-2" : "space-y-3")}>
      {!isSidebar ? (
        <p className="text-sm font-medium text-muted-foreground">Szablon oferty dla klienta</p>
      ) : null}
      <div
        className={cn("grid w-full gap-2", isSidebar && "grid-cols-1")}
        style={
          isSidebar ? undefined : { gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }
        }
      >
        {OFFER_TEMPLATES.map((t) => {
          const isSelected = t.id === value;
          return (
            <div
              key={t.id}
              className={cn(
                "flex flex-col gap-1 rounded-lg p-1 transition-shadow",
                isSelected && "shadow-sm ring-1 ring-[#e76f51]/25"
              )}
              style={{
                borderWidth: 2,
                borderStyle: "solid",
                borderColor: isSelected ? SELECTED_BORDER : "transparent",
              }}
            >
              <button
                type="button"
                onClick={() => onChange(t.id)}
                className={cn(
                  "flex w-full flex-col items-stretch gap-1 rounded-md text-left",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
                aria-pressed={isSelected}
                aria-label={`Wybierz szablon: ${t.name}`}
              >
                <div
                  className={cn(
                    "relative w-full overflow-hidden rounded-md",
                    isSidebar ? "h-[72px] max-h-[72px]" : "h-[100px] max-h-[100px]"
                  )}
                >
                  <div className="absolute inset-0" style={{ backgroundColor: t.colors.headerBg }} />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[14%] min-h-[6px]"
                    style={{ backgroundColor: t.colors.accentColor }}
                  />
                  <div
                    className="absolute left-[10%] top-[18%] h-[8%] w-[55%] rounded-sm opacity-90"
                    style={{ backgroundColor: t.colors.borderColor }}
                  />
                  <div
                    className="absolute left-[10%] top-[32%] h-[4%] w-[70%] rounded-sm opacity-60"
                    style={{ backgroundColor: t.colors.metaText }}
                  />
                  <div
                    className="absolute left-[10%] top-[40%] h-[4%] w-[62%] rounded-sm opacity-50"
                    style={{ backgroundColor: t.colors.metaText }}
                  />
                  <div
                    className="absolute bottom-[18%] left-[10%] right-[10%] h-[16%] rounded-sm"
                    style={{ backgroundColor: t.colors.totalBg }}
                  />
                  {isSelected ? (
                    <span
                      className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-background shadow-md"
                      aria-hidden
                    >
                      <Check className="h-3 w-3" strokeWidth={3} style={{ color: SELECTED_BORDER }} />
                    </span>
                  ) : null}
                </div>
                <span
                  className={cn(
                    "line-clamp-2 text-center font-medium leading-snug text-foreground",
                    isSidebar ? "text-[10px]" : "text-[11px]"
                  )}
                >
                  {t.name}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
